import { useBranchCircuitStatus, useGetTopFiveElectricalEnergyUsage } from 'common';
import { LucideThumbsUp, LucideXCircle } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { XStack } from 'tamagui';
import { gradients } from 'tamagui-config';

import { WidgetsWrapper } from '../../components/common';
import {
  DraggableItemData,
  DraggableWidgetCard,
} from '../../components/common/DraggableWidgetCard';
import { DraggableWrapper } from '../../components/common/DraggableWrapper';
import { useAppSync } from '../../contexts/AppSyncContext';
import {
  ElectricalAlertProvider,
  ElectricalAlertState,
  useElectricalAlertContext,
} from '../../contexts/ElectricalAlertContext';
import {
  useDraggingContext,
  useGetEnergySavingData,
  useGetRawElectricalData,
  usePowerMonitorSystemHealth,
  useWidgetOrder,
} from '../../hooks';
import { ElectricalAlertContent } from './components/ElectricalAlertContent';
import { ElectricalStatusContent } from './components/ElectricalStatusContent';
import { EnergySavingContent } from './components/EnergySavingContent';
import { RawElectricalDataContent } from './components/RawElectricalDataContent';
import { TodaysEnergyCostContent } from './components/TodaysEnergyCostContent';
import { TopLiveElectricalUsageContent } from './components/TopLiveElectricalUsageContent';

enum ElectricalWidgetCardTypes {
  ElectricalStatus = 'ElectricalStatus',
  ElectricalAlert = 'ElectricalAlert',
  TodaysEnergyCost = 'TodaysEnergyCost',
  EnergySavings = 'EnergySavings',
  TopLiveElectricalUsage = 'TopLiveElectricalUsage',
  RawElectricalData = 'RawElectricalData',
  BranchCircuit = 'BranchCircuit',
}

const ElectricalContent: React.FC = () => {
  const { t } = useTranslation();
  const { selectedHubId, sensors, hub } = useAppSync();
  const systemHealthStatus = usePowerMonitorSystemHealth();
  const { branchCircuits } = useBranchCircuitStatus({ selectedHubId });
  const { devices: topFiveDevices } = useGetTopFiveElectricalEnergyUsage({
    selectedHub: hub,
    sensors,
  });
  const {
    data: rawElectricalData,
    isValidConfiguration: isValidRawElectricalDataConfiguration,
    loading: rawElectricalDataLoading,
  } = useGetRawElectricalData();
  const { sensor: energySavingSensor } = useGetEnergySavingData();
  const { alertState } = useElectricalAlertContext();
  const { isDraggable } = useDraggingContext();

  const data: DraggableItemData<ElectricalWidgetCardTypes>[] = useMemo(() => {
    const arr = [
      {
        id: '1',
        title:
          alertState === ElectricalAlertState.ServiceRequested
            ? t('electrical.serviceRequested')
            : alertState === ElectricalAlertState.Alert
              ? t('electrical.criticalAlert')
              : t('electrical.allSystemsNominal'),
        type: ElectricalWidgetCardTypes.ElectricalAlert,
      },
      {
        id: '2',
        title: t('electrical.systemHealth'),
        type: ElectricalWidgetCardTypes.ElectricalStatus,
      },
      {
        id: '3',
        title: t('electrical.topLiveUsage'),
        type: ElectricalWidgetCardTypes.TopLiveElectricalUsage,
      },
      {
        id: '4',
        title: t('electrical.todaysEnergyCost'),
        type: ElectricalWidgetCardTypes.TodaysEnergyCost,
      },
      {
        id: '5',
        title: t('electrical.energySavings'),
        type: ElectricalWidgetCardTypes.EnergySavings,
      },
      {
        id: '6',
        title: t('electrical.rawElectricalData.title'),
        type: ElectricalWidgetCardTypes.RawElectricalData,
      },
      ...branchCircuits.map((circuit) => ({
        id: `branch-${circuit.location}`,
        title: circuit.location,
        type: ElectricalWidgetCardTypes.BranchCircuit,
        circuit,
      })),
    ];
    return arr;
  }, [alertState, t, branchCircuits]);

  const getTimestampForWidgetType = (
    type: ElectricalWidgetCardTypes | undefined,
    item?: any,
  ): string | undefined => {
    switch (type) {
      case ElectricalWidgetCardTypes.ElectricalStatus:
        return systemHealthStatus.timestamp
          ? t('electrical.timestamp', { date: systemHealthStatus.timestamp * 1000 })
          : undefined;
      case ElectricalWidgetCardTypes.RawElectricalData:
        return rawElectricalData?.[0]?.timestamp
          ? t('electrical.timestamp', { date: rawElectricalData[0].timestamp * 1000 })
          : undefined;
      case ElectricalWidgetCardTypes.TodaysEnergyCost:
        return energySavingSensor?.lastReported
          ? t('electrical.timestamp', { date: energySavingSensor.lastReported * 1000 })
          : undefined;
      case ElectricalWidgetCardTypes.EnergySavings:
        return energySavingSensor?.lastReported
          ? t('electrical.timestamp', { date: energySavingSensor.lastReported * 1000 })
          : undefined;
      case ElectricalWidgetCardTypes.BranchCircuit:
        return item?.circuit?.lastReported
          ? t('electrical.timestamp', { date: item.circuit.lastReported * 1000 })
          : undefined;
      default:
        return undefined;
    }
  };

  const getErrorForWidgetType = (type: ElectricalWidgetCardTypes | undefined): boolean => {
    switch (type) {
      case ElectricalWidgetCardTypes.RawElectricalData:
        return (
          !isValidRawElectricalDataConfiguration ||
          !rawElectricalData ||
          rawElectricalData.length === 0
        );
      default:
        return false;
    }
  };

  const {
    items,
    handleItemsReordered,
    handleStartDragging,
    handleSaveOrder,
    handleCancelOrder,
    isItemEnabled,
    setItemEnabled,
  } = useWidgetOrder<DraggableItemData<ElectricalWidgetCardTypes>>('electricalWidgetsOrder', data);

  const renderItem = (item: DraggableItemData<ElectricalWidgetCardTypes>, index: number) => {
    const isNoAlertCard =
      item.type === ElectricalWidgetCardTypes.ElectricalAlert &&
      alertState === ElectricalAlertState.NoAlert;

    const isTopLiveUsageWithoutData =
      item.type === ElectricalWidgetCardTypes.TopLiveElectricalUsage &&
      (!topFiveDevices || topFiveDevices.length === 0);

    if (!isDraggable && (isNoAlertCard || isTopLiveUsageWithoutData)) {
      return null;
    }

    const isEnergyWidget =
      item.type === ElectricalWidgetCardTypes.TodaysEnergyCost ||
      item.type === ElectricalWidgetCardTypes.EnergySavings;

    return (
      <DraggableWidgetCard
        key={item.id}
        item={item}
        index={index}
        enabled={isItemEnabled(item.id)}
        timestamp={getTimestampForWidgetType(item.type, item)}
        error={getErrorForWidgetType(item.type)}
        hideArrowIcon={isEnergyWidget}
        onEnabledChange={(enabled) => setItemEnabled(item.id, enabled)}>
        {item.type && renderItemContent(item.type, item)}
      </DraggableWidgetCard>
    );
  };

  const renderItemContent = (type: ElectricalWidgetCardTypes | string, item?: any) => {
    const getElectricalGradient = (status: string) => {
      switch (status) {
        case 'good':
          return gradients.success;
        case 'poor':
          return gradients.warning;
        case 'error':
        default:
          return gradients.error;
      }
    };
    
    const getElectricalHealthStatus = (status: string) => {
      switch (status) {
        case 'good':
          return t('electrical.status.healthy');
        case 'poor':
          return t('electrical.status.unhealthy');
        case 'error':
        default:
          return t('electrical.status.error');
      }
    };

    const getElectricalIcon = () => {
      switch (alertState) {
        case ElectricalAlertState.ServiceRequested:
          return <LucideThumbsUp size={64} />;
        case ElectricalAlertState.Alert:
          return <LucideXCircle size={64} />;
        case ElectricalAlertState.NoAlert:
        default:
          return <LucideThumbsUp size={64} />;
      }
    };

    if (type === ElectricalWidgetCardTypes.BranchCircuit && item?.circuit) {
      const circuit = item.circuit;
      return (
        <ElectricalStatusContent
          key={circuit.location}
          statusIcon={getElectricalIcon()}
          statusLabel={getElectricalHealthStatus(circuit.status)}
          gradientColors={getElectricalGradient(circuit.status)}
          roomLocation={circuit.location}
          roomAlerts={circuit.alert ? [circuit.alert] : []}
        />
      );
    }

    switch (type) {
      case ElectricalWidgetCardTypes.ElectricalStatus: {
        const offlineRoomsCount = branchCircuits.filter(
          (circuit) => circuit.status === 'error' || circuit.status === 'poor',
        ).length;
        const { status, loading } = systemHealthStatus;
        // if (status !== 'good' && loading !== true) {
        //   console.log('System Health Status:', systemHealthStatus);
        //   console.log('Branch Circuits:', branchCircuits);
        //   console.log('Offline Rooms Count:', offlineRoomsCount);
        // }
        // if (offlineRoomsCount > 0) {
        //   console.log('Offline Rooms Count:', offlineRoomsCount);
        //   console.log('Branch Circuits:', branchCircuits);
        // }
        
        return (
          <ElectricalStatusContent
            key={ElectricalWidgetCardTypes.ElectricalStatus}
            statusIcon={getElectricalIcon()}
            statusLabel={getElectricalHealthStatus(status)}
            gradientColors={getElectricalGradient(status)}
            offlineRoomsCount={offlineRoomsCount}
            loading={loading}
          />
        );
      }
      case ElectricalWidgetCardTypes.ElectricalAlert:
        return <ElectricalAlertContent key={ElectricalWidgetCardTypes.ElectricalAlert} />;
      case ElectricalWidgetCardTypes.TopLiveElectricalUsage:
        return topFiveDevices && topFiveDevices.length > 0 ? (
          <TopLiveElectricalUsageContent
            key={ElectricalWidgetCardTypes.TopLiveElectricalUsage}
            data={topFiveDevices}
          />
        ) : null;
      case ElectricalWidgetCardTypes.TodaysEnergyCost:
        return <TodaysEnergyCostContent />;
      case ElectricalWidgetCardTypes.EnergySavings:
        return <EnergySavingContent />;
      case ElectricalWidgetCardTypes.RawElectricalData:
        return (
          <RawElectricalDataContent
            data={rawElectricalData}
            isValidConfiguration={isValidRawElectricalDataConfiguration}
            isLoading={rawElectricalDataLoading}
          />
        );
    }
  };

  return (
    <WidgetsWrapper
      onStartDragging={handleStartDragging}
      onSaveOrder={handleSaveOrder}
      onCancelOrder={handleCancelOrder}>
      <XStack flex={1}>
        <DraggableWrapper items={items} onItemsReordered={handleItemsReordered}>
          {items.map(renderItem)}
        </DraggableWrapper>
      </XStack>
    </WidgetsWrapper>
  );
};

export const Electrical: React.FC = () => (
  <ElectricalAlertProvider>
    <ElectricalContent />
  </ElectricalAlertProvider>
);
